import React from 'react';
import { splitRoles, flagsHidden, flagsToBeRemoved, flagRemovalNotice, requiresRoleHint } from '../../utils/roleFlags';

// Role picker for the user create/update modals. Flag roles (see utils/roleFlags)
// are listed separately as checkboxes and hidden while an admin-account role is
// selected (the backend refuses that combination). `selected` holds every id —
// roles and flags — because the backend full-replaces the user's role set on save,
// so a flag the user already holds must stay in the submitted list. When an
// admin-account pick means a held flag will be dropped on save, say so up front.
const RoleChecklist = ({ roles, selected, onChange }) => {
    const { roles: plainRoles, flags } = splitRoles(roles);
    const plainIds = selected.filter((id) => plainRoles.some((r) => r.role_id === id));
    const hideFlags = flagsHidden(plainIds, roles);
    const removalNotice = flagRemovalNotice(flagsToBeRemoved(selected, roles));

    const toggle = (roleId, checked) =>
        onChange(checked ? [...new Set([...selected, roleId])] : selected.filter((id) => id !== roleId));

    const renderBox = (role) => {
        const hint = requiresRoleHint(role);
        const hintId = hint ? `role-hint-${role.role_id}` : undefined;
        return (
            <div key={role.role_id}>
                <label className="flex items-center gap-2 text-sm">
                    <input
                        type="checkbox"
                        checked={selected.includes(role.role_id)}
                        onChange={(e) => toggle(role.role_id, e.target.checked)}
                        aria-describedby={hintId}
                        className="rounded border-slate-300"
                    />
                    <span>{role.role_name}</span>
                </label>
                {hint && (
                    <p id={hintId} className="text-xs text-slate-500 ml-6">
                        {hint}
                    </p>
                )}
            </div>
        );
    };

    return (
        <>
            <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Roles *</label>
                <div className="space-y-2 max-h-32 overflow-y-auto border border-slate-200 rounded-lg p-2">
                    {plainRoles.map(renderBox)}
                </div>
                {plainIds.length === 0 && <p className="text-xs text-red-500 mt-1">At least one role is required</p>}
            </div>
            {flags.length > 0 && !hideFlags && (
                <fieldset>
                    <legend className="block text-sm font-medium text-slate-700 mb-1">Flags</legend>
                    <div className="space-y-2 border border-slate-200 rounded-lg p-2">{flags.map(renderBox)}</div>
                    <p className="text-xs text-slate-500 mt-1">
                        Flags are optional markers added alongside a role. Admin accounts cannot have flags.
                    </p>
                </fieldset>
            )}
            {removalNotice && (
                <p
                    role="status"
                    className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2"
                >
                    {removalNotice}. Admin accounts cannot have flags.
                </p>
            )}
        </>
    );
};

export default RoleChecklist;
